// src/components/site-editor/panels/LogoPanel.js
import React, { useState, useRef } from 'react';
import { Upload, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import { getPractitionerDisplayName } from '../../../utils/getPractitionerDisplayName';
import InlineCallout from '../InlineCallout';
import ToggleGroup from '../../common/ToggleGroup';
import { uploadFile, generateFileName, deleteFile } from '../../../services/storage-service';

const LogoPanel = () => {
  const { state, actions } = useWebsiteBuilder();
  const logo = state.draft.contactFooter.logo || {
    type: 'styledText', // 'upload' | 'styledText' | 'none'
    uploadedImage: null,
    styledTextFont: 'elegant'
  };

  const [selectedOption, setSelectedOption] = useState(logo.type || 'styledText');
  const [uploadedImage, setUploadedImage] = useState(logo.uploadedImage || null);
  const [uploadedImagePath, setUploadedImagePath] = useState(logo.uploadedImagePath || null);
  const [styledTextFont, setStyledTextFont] = useState(logo.styledTextFont || 'elegant');
  const [showCanvaModal, setShowCanvaModal] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Image editing controls
  const [logoSize, setLogoSize] = useState(logo.logoSize || 'medium');
  const [removeBackground, setRemoveBackground] = useState(logo.removeBackground || false);

  const fileInputRef = useRef(null);

  // Get practitioner display name from all possible sources
  const displayName = getPractitionerDisplayName(state) || 'Your Name';

  const fontStyles = [
    {
      value: 'elegant',
      label: 'Elegant',
      font: "'Playfair Display', serif",
      preview: 'Aa'
    },
    {
      value: 'modern',
      label: 'Modern',
      font: "'Montserrat', sans-serif",
      preview: 'Aa'
    },
    {
      value: 'bold',
      label: 'Bold',
      font: "'Poppins', sans-serif",
      preview: 'Aa'
    },
    {
      value: 'handwritten',
      label: 'Handwritten',
      font: "'Dancing Script', cursive",
      preview: 'Aa'
    }
  ];

  const handleOptionChange = (option) => {
    setSelectedOption(option);

    if (option === 'styledText') {
      // Clean up Storage logo when switching away from upload
      if (uploadedImagePath) {
        deleteFile(uploadedImagePath).catch(err =>
          console.warn('⚠️ Failed to delete logo from Storage:', err)
        );
        setUploadedImage(null);
        setUploadedImagePath(null);
      }
      actions.updateContactFooter('logo', {
        type: 'styledText',
        styledTextFont: styledTextFont,
        uploadedImage: null,
        uploadedImagePath: null,
        logoSize: null
      });
    } else if (option === 'upload' && uploadedImage) {
      actions.updateContactFooter('logo', {
        type: 'upload',
        uploadedImage: uploadedImage,
        uploadedImagePath: uploadedImagePath,
        styledTextFont: null,
        logoSize: logoSize,
        removeBackground: removeBackground,
      });
    }
  };

  const handleLogoSizeChange = (size) => {
    setLogoSize(size);
    actions.updateContactFooter('logo', {
      type: 'upload',
      uploadedImage: uploadedImage,
      uploadedImagePath: uploadedImagePath,
      styledTextFont: null,
      logoSize: size,
      removeBackground: removeBackground,
    });
  };

  const handleRemoveBackgroundToggle = (enabled) => {
    setRemoveBackground(enabled);
    actions.updateContactFooter('logo', {
      type: 'upload',
      uploadedImage: uploadedImage,
      uploadedImagePath: uploadedImagePath,
      styledTextFont: null,
      logoSize: logoSize,
      removeBackground: enabled,
    });
  };

  // Helper function to compress and convert image to base64
  const compressAndConvertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      img.onload = () => {
        // Compress to 200x200px max (logos don't need to be large)
        const maxSize = 200;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 with compression (0.8 quality = ~10-30KB)
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(base64);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Please upload an image file (JPG, PNG, SVG, or WEBP)');
      return;
    }

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      setUploadError('Image must be less than 2MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    // Clean up previous logo from Storage before uploading new one
    if (uploadedImagePath) {
      deleteFile(uploadedImagePath).catch(err =>
        console.warn('⚠️ Failed to delete old logo from Storage:', err)
      );
    }

    try {
      const fileName = generateFileName(file.name || 'logo.png');
      const { url, fullPath } = await uploadFile(`logos/${fileName}`, file);
      setUploadedImage(url);
      setUploadedImagePath(fullPath);
      setSelectedOption('upload');

      actions.updateContactFooter('logo', {
        type: 'upload',
        uploadedImage: url,
        uploadedImagePath: fullPath,
        styledTextFont: null,
        logoSize: logoSize,
        removeBackground: removeBackground,
      });

    } catch (error) {
      // Fall back to Base64 with compression
      console.warn('⚠️ Storage upload failed, using Base64 fallback:', error.message);

      try {
        const base64Url = await compressAndConvertToBase64(file);

        setUploadedImage(base64Url);
        setUploadedImagePath(null);
        setSelectedOption('upload');

        actions.updateContactFooter('logo', {
          type: 'upload',
          uploadedImage: base64Url,
          uploadedImagePath: null,
          styledTextFont: null,
          logoSize: logoSize,
          removeBackground: removeBackground,
        });
      } catch (base64Error) {
        console.error('❌ Upload error:', base64Error);
        setUploadError(base64Error.message || 'Failed to upload logo');
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveUpload = () => {
    // Delete from Storage in background
    if (uploadedImagePath) {
      deleteFile(uploadedImagePath).catch(err =>
        console.warn('⚠️ Failed to delete logo from Storage:', err)
      );
    }

    setUploadedImage(null);
    setUploadedImagePath(null);
    setSelectedOption('styledText');
    setLogoSize('medium');

    actions.updateContactFooter('logo', {
      type: 'styledText',
      styledTextFont: styledTextFont,
      uploadedImage: null,
      uploadedImagePath: null,
      logoSize: null
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFontChange = (font) => {
    setStyledTextFont(font);

    if (selectedOption === 'styledText') {
      actions.updateContactFooter('logo', {
        type: 'styledText',
        styledTextFont: font,
        uploadedImage: null
      });
    }
  };

  const handleCanvaClick = () => {
    // Check if there are unsaved changes
    const hasChanges = state.hasChanges || false;

    if (hasChanges) {
      setShowCanvaModal(true);
    } else {
      window.open('https://www.canva.com/create/logos/', '_blank', 'noopener,noreferrer');
    }
  };

  const handleCanvaConfirm = () => {
    setShowCanvaModal(false);
    window.open('https://www.canva.com/create/logos/', '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Logo</h3>
        <p className="text-sm text-gray-600">
          Choose how you want to represent your brand. You can upload an existing logo, use your styled name, or create a new logo with Canva.
        </p>
      </div>

      {/* Inline Callout */}
      <InlineCallout
        text="Your logo is your visual identity. Learn how to choose or create a logo that reflects your practice authentically."
        playbookTopic="building-your-presence"
      />

      {/* Logo Options */}
      <div className="space-y-4">
        {/* Option 1: Upload Logo */}
        <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 transition-colors">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="logoOption"
              value="upload"
              checked={selectedOption === 'upload'}
              onChange={() => handleOptionChange('upload')}
              className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
            />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Upload Existing Logo</h4>
              <p className="text-xs text-gray-600 mb-3">
                Upload your logo image (max 2MB)
              </p>

              {selectedOption === 'upload' && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  {/* Hidden file input - always present so "Change Image" can access it */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />

                  {!uploadedImage ? (
                    <>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 border border-[#E5E7EB] rounded-lg text-sm font-medium transition-colors ${
                          isUploading
                            ? 'opacity-50 cursor-wait text-gray-500'
                            : 'text-gray-700 hover:border-purple-300 hover:text-purple-600 hover:bg-purple-50'
                        }`}
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Choose File
                          </>
                        )}
                      </button>
                      {uploadError && (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {uploadError}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className="space-y-4">
                      {/* Uploaded Image Preview */}
                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
                        <div className="flex items-center justify-center p-4 bg-white rounded">
                          <img
                            src={uploadedImage}
                            alt="Uploaded logo"
                            style={{
                              maxWidth: '150px',
                              height: logoSize === 'small' ? '32px' : logoSize === 'large' ? '64px' : '48px',
                              objectFit: 'contain'
                            }}
                          />
                        </div>
                      </div>

                      {/* Logo Size Control */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Logo Size</label>
                        <ToggleGroup
                          options={[
                            { value: 'small', label: 'Small' },
                            { value: 'medium', label: 'Medium' },
                            { value: 'large', label: 'Large' }
                          ]}
                          value={logoSize}
                          onChange={handleLogoSizeChange}
                        />
                      </div>

                      {/* Remove Background Toggle */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                            Remove logo background
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveBackgroundToggle(!removeBackground)}
                            style={{
                              width: '40px', height: '22px', borderRadius: '11px',
                              backgroundColor: removeBackground ? '#7c3aed' : '#D1D5DB',
                              border: 'none', cursor: 'pointer', position: 'relative',
                              transition: 'background-color 0.2s ease', padding: 0, flexShrink: 0,
                            }}
                          >
                            <div style={{
                              width: '18px', height: '18px', borderRadius: '50%',
                              backgroundColor: 'white', position: 'absolute', top: '2px',
                              left: removeBackground ? '20px' : '2px',
                              transition: 'left 0.2s ease',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                            }} />
                          </button>
                        </div>
                        <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>
                          Works best with logos on white backgrounds
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                        >
                          Change Image
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveUpload}
                          className="text-sm font-medium text-gray-500 hover:text-red-600 transition-colors"
                        >
                          Remove Logo
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Pro tip — always visible when upload option is selected */}
                  <div style={{ backgroundColor: '#F9FAFB', borderRadius: '8px', padding: '12px' }}>
                    <p style={{ fontSize: '13px', color: '#6B7280', margin: 0, lineHeight: '1.5' }}>
                      💡 Pro tip: For the best look, use a PNG with a transparent background. You can remove backgrounds for free at{' '}
                      <a
                        href="https://www.remove.bg"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#7c3aed', textDecoration: 'none', transition: 'color 0.15s ease' }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#6D28D9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#7c3aed'; }}
                      >
                        remove.bg →
                      </a>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        {/* Option 2: Styled Text */}
        <div className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 transition-colors">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="logoOption"
              value="styledText"
              checked={selectedOption === 'styledText'}
              onChange={() => handleOptionChange('styledText')}
              className="mt-1 w-4 h-4 text-purple-600 focus:ring-purple-500"
            />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Use Styled Text</h4>
              <p className="text-xs text-gray-600 mb-3">
                Don't have a logo yet? Display your name in a beautiful font
              </p>

              {selectedOption === 'styledText' && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <label className="block text-sm font-medium text-gray-900">
                    Font Style
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {fontStyles.map((style) => (
                      <button
                        key={style.value}
                        type="button"
                        onClick={() => handleFontChange(style.value)}
                        className={`p-3 rounded-lg border-2 transition-all text-center ${
                          styledTextFont === style.value
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div
                          className="text-2xl mb-1"
                          style={{ fontFamily: style.font }}
                        >
                          {style.preview}
                        </div>
                        <p className="text-xs font-medium text-gray-900">{style.label}</p>
                      </button>
                    ))}
                  </div>

                  {/* Live Preview */}
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
                    <div
                      className="text-2xl font-bold text-gray-900 text-center"
                      style={{
                        fontFamily: fontStyles.find(s => s.value === styledTextFont)?.font
                      }}
                    >
                      {displayName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </label>
        </div>

        {/* Option 3: Create with Canva */}
        <div style={{
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          padding: '16px',
          background: 'white',
          transition: 'border-color 0.15s'
        }}>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">Create with Canva</h4>
          <p className="text-xs text-gray-600 mb-3">
            Use Canva's free logo maker with practitioner-friendly templates
          </p>
          <button
            type="button"
            onClick={handleCanvaClick}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#7c3aed',
              background: '#F5F3FF',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#EDE9FE'}
            onMouseLeave={(e) => e.target.style.background = '#F5F3FF'}
          >
            <ExternalLink style={{ width: '16px', height: '16px' }} />
            Open Canva Logo Maker
          </button>
          <p className="text-xs text-gray-500 mt-2">
            After creating your logo in Canva, download it and upload here
          </p>
        </div>

      </div>

      {/* Canva Unsaved Changes Modal */}
      {showCanvaModal && (
        <CanvaWarningModal
          onConfirm={handleCanvaConfirm}
          onCancel={() => setShowCanvaModal(false)}
        />
      )}
    </div>
  );
};

// Canva Warning Modal Component
const CanvaWarningModal = ({ onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ExternalLink className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Leaving Site Editor</h3>
            <p className="text-sm text-gray-600">
              You're about to open Canva in a new tab. You have unsaved changes in the Site Editor.
            </p>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
          <p className="text-xs text-amber-800">
            <strong>Tip:</strong> Save or publish your changes before leaving to avoid losing your work.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Stay Here
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
          >
            Continue to Canva
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogoPanel;
